import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Plus, 
    Phone,
    Mail,
    Trash2,
    Building2,
    Shield,
    Users,
    AlertTriangle,
    Radio,
    UserCheck,
    Upload,
    FileSpreadsheet,
    Check,
    X
} from 'lucide-react';
import { toast } from 'sonner';
import { RoleLabels } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
    { value: 'equipe', label: 'Equipe PCS', icon: UserCheck, color: 'text-emerald-500' },
    { value: 'pompiers', label: 'Pompiers', icon: AlertTriangle, color: 'text-red-500' },
    { value: 'gendarmerie', label: 'Gendarmerie', icon: Shield, color: 'text-blue-500' },
    { value: 'mairie', label: 'Mairie', icon: Building2, color: 'text-green-500' },
    { value: 'prefet', label: 'Prefecture', icon: Building2, color: 'text-purple-500' },
    { value: 'personnalite_communale', label: 'Personnalites communales', icon: Users, color: 'text-amber-500' },
    { value: 'associations', label: 'Associations', icon: Users, color: 'text-cyan-500' },
    { value: 'sante', label: 'Sante', icon: AlertTriangle, color: 'text-pink-500' },
    { value: 'autre', label: 'Autre', icon: Phone, color: 'text-zinc-400' }
];

const CrisisDirectoryPage = () => {
    const { token, isChefDispositif } = useAuth();
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('all');
    
    const [formData, setFormData] = useState({
        category: 'pompiers',
        name: '',
        role: '',
        phone: '',
        email: '',
        notes: ''
    });

    // Import state
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importPreview, setImportPreview] = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [duplicateAction, setDuplicateAction] = useState({}); // { index: 'skip' | 'overwrite' }

    const headers = { Authorization: `Bearer ${token}` };

    const fetchContacts = async () => {
        try {
            const [contactsRes, usersRes] = await Promise.all([
                axios.get(`${API}/crisis-directory`, { headers }),
                axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] }))
            ]);
            
            // Auto-generate contacts from registered users (equipe PCS)
            const userContacts = (usersRes.data || [])
                .filter(u => u.role !== 'pending' && (u.phone || u.radio_channel))
                .map(u => ({
                    id: `user_${u.id}`,
                    category: 'equipe',
                    name: u.name || u.email,
                    role: RoleLabels[u.role] || u.role,
                    phone: u.phone || '',
                    email: u.email || '',
                    radio_channel: u.radio_channel || '',
                    notes: '',
                    is_user: true
                }));
            
            setContacts([...userContacts, ...contactsRes.data]);
        } catch (error) {
            console.error('Failed to fetch contacts:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchContacts();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/crisis-directory`, formData, { headers });
            toast.success('Contact ajouté');
            setDialogOpen(false);
            resetForm();
            fetchContacts();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer ce contact ?')) return;
        try {
            await axios.delete(`${API}/crisis-directory/${id}`, { headers });
            toast.success('Contact supprimé');
            fetchContacts();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const resetForm = () => {
        setFormData({ category: 'pompiers', name: '', role: '', phone: '', email: '', notes: '' });
    };

    const handleImportUpload = async () => {
        if (!importFile) return;
        setImportLoading(true);
        try {
            const fd = new FormData();
            fd.append('file', importFile);
            const res = await axios.post(`${API}/crisis-directory/import`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
            setImportPreview(res.data);
            setDuplicateAction({});
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lecture fichier');
        } finally { setImportLoading(false); }
    };

    const handleImportConfirm = async () => {
        if (!importPreview) return;
        setImportLoading(true);
        try {
            const overwrite = importPreview.duplicates.filter((_, i) => duplicateAction[i] === 'overwrite');
            await axios.post(`${API}/crisis-directory/import/confirm`, {
                contacts: importPreview.new_contacts,
                overwrite
            }, { headers });
            const skipped = importPreview.duplicates.length - overwrite.length;
            toast.success(`Import termine : ${importPreview.new_contacts.length} ajoute(s), ${overwrite.length} mis a jour, ${skipped} ignore(s)`);
            setImportDialogOpen(false);
            setImportFile(null);
            setImportPreview(null);
            fetchContacts();
        } catch { toast.error('Erreur import'); }
        finally { setImportLoading(false); }
    };

    const filteredContacts = selectedCategory === 'all' 
        ? contacts 
        : contacts.filter(c => c.category === selectedCategory);

    // Group contacts by category
    const groupedContacts = categories.map(cat => ({
        ...cat,
        contacts: contacts.filter(c => c.category === cat.value)
    })).filter(g => g.contacts.length > 0);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="crisis-directory-page">
            <div className="flex items-center justify-between">
                <div className="page-header">
                    <h1 className="page-title">Annuaire de Crise</h1>
                    <p className="page-subtitle">Contacts d'urgence et personnalités clés</p>
                </div>

                {isChefDispositif && (
                    <div className="flex items-center gap-2">
                        <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 gap-2" onClick={() => { setImportFile(null); setImportPreview(null); setImportDialogOpen(true); }} data-testid="import-directory-btn">
                            <Upload className="w-4 h-4" /> Importer
                        </Button>
                        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                        <DialogTrigger asChild>
                            <Button className="tactical-btn" data-testid="add-contact-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Ajouter Contact
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md bg-zinc-900 border-zinc-800">
                            <DialogHeader>
                                <DialogTitle className="text-xl font-semibold text-white">Nouveau contact</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="form-group">
                                    <Label className="form-label">Catégorie</Label>
                                    <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                                        <SelectTrigger className="tactical-input">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-900 border-zinc-800">
                                            {categories.map(cat => (
                                                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="form-group">
                                        <Label className="form-label">Nom</Label>
                                        <Input
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="tactical-input"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label className="form-label">Fonction</Label>
                                        <Input
                                            value={formData.role}
                                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                            className="tactical-input"
                                            placeholder="Ex: Commandant"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="form-group">
                                        <Label className="form-label">Téléphone</Label>
                                        <Input
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            className="tactical-input"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label className="form-label">Email</Label>
                                        <Input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className="tactical-input"
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2 pt-4">
                                    <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Annuler</Button>
                                    <Button type="submit" className="tactical-btn">Ajouter</Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                    </div>
                )}

                {/* Import Dialog */}
                <Dialog open={importDialogOpen} onOpenChange={(open) => { setImportDialogOpen(open); if (!open) { setImportFile(null); setImportPreview(null); } }}>
                    <DialogContent className="max-w-2xl bg-zinc-900 border-zinc-800 max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="text-xl font-semibold text-white flex items-center gap-2">
                                <FileSpreadsheet className="w-5 h-5 text-emerald-400" /> Importer un annuaire
                            </DialogTitle>
                        </DialogHeader>

                        {!importPreview ? (
                            <div className="space-y-4">
                                <p className="text-sm text-zinc-400">
                                    Importez un fichier CSV ou Excel contenant vos contacts. Colonnes attendues :
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {['Nom', 'Role/Fonction', 'Organisation', 'Telephone', 'Email', 'Categorie', 'Notes'].map(col => (
                                        <span key={col} className="px-2 py-0.5 bg-zinc-800 rounded text-xs text-zinc-300 font-mono">{col}</span>
                                    ))}
                                </div>
                                <p className="text-xs text-zinc-500">
                                    Categories valides : pompiers, gendarmerie, mairie, prefet, personnalite_communale, associations, sante, autre
                                </p>
                                <div className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center hover:border-emerald-500/50 transition-colors cursor-pointer"
                                     onClick={() => document.getElementById('import-file-input').click()}>
                                    <Upload className="w-8 h-8 mx-auto mb-2 text-zinc-500" />
                                    {importFile ? (
                                        <p className="text-sm text-emerald-400 font-medium">{importFile.name}</p>
                                    ) : (
                                        <p className="text-sm text-zinc-500">Cliquez pour selectionner un fichier<br /><span className="text-[10px]">CSV ou Excel (.xlsx)</span></p>
                                    )}
                                </div>
                                <input id="import-file-input" type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setImportFile(e.target.files[0])} className="hidden" data-testid="import-file-input" />
                                <div className="flex justify-end gap-2">
                                    <Button variant="ghost" onClick={() => setImportDialogOpen(false)}>Annuler</Button>
                                    <Button onClick={handleImportUpload} className="tactical-btn" disabled={!importFile || importLoading} data-testid="import-upload-btn">
                                        {importLoading ? 'Analyse...' : 'Analyser le fichier'}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {/* Summary */}
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="p-3 bg-zinc-800/50 rounded text-center">
                                        <p className="text-2xl font-bold text-white">{importPreview.parsed}</p>
                                        <p className="text-xs text-zinc-500">Contacts lus</p>
                                    </div>
                                    <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded text-center">
                                        <p className="text-2xl font-bold text-emerald-400">{importPreview.new_contacts.length}</p>
                                        <p className="text-xs text-zinc-500">Nouveaux</p>
                                    </div>
                                    <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded text-center">
                                        <p className="text-2xl font-bold text-amber-400">{importPreview.duplicates.length}</p>
                                        <p className="text-xs text-zinc-500">Doublons</p>
                                    </div>
                                </div>

                                {/* New contacts preview */}
                                {importPreview.new_contacts.length > 0 && (
                                    <div>
                                        <p className="text-sm font-medium text-emerald-400 mb-2">Nouveaux contacts ({importPreview.new_contacts.length})</p>
                                        <div className="max-h-[150px] overflow-y-auto border border-zinc-800 rounded">
                                            <table className="w-full text-xs">
                                                <thead><tr className="bg-zinc-800 text-zinc-400"><th className="p-1.5 text-left">Nom</th><th className="p-1.5 text-left">Role</th><th className="p-1.5 text-left">Tel</th><th className="p-1.5 text-left">Cat.</th></tr></thead>
                                                <tbody>
                                                    {importPreview.new_contacts.map((c) => (
                                                        <tr key={`${c.name}-${c.phone}`} className="border-t border-zinc-800/50">
                                                            <td className="p-1.5 text-white">{c.name}</td>
                                                            <td className="p-1.5 text-zinc-400">{c.role}</td>
                                                            <td className="p-1.5 text-zinc-400">{c.phone}</td>
                                                            <td className="p-1.5 text-zinc-500">{c.category}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Duplicates */}
                                {importPreview.duplicates.length > 0 && (
                                    <div>
                                        <p className="text-sm font-medium text-amber-400 mb-2">Doublons detectes ({importPreview.duplicates.length}) - Que faire ?</p>
                                        <div className="max-h-[200px] overflow-y-auto space-y-1">
                                            {importPreview.duplicates.map((c, i) => (
                                                <div key={`${c.name}-${c.phone}`} className={`flex items-center justify-between p-2 rounded border ${duplicateAction[i] === 'overwrite' ? 'bg-amber-500/10 border-amber-500/30' : duplicateAction[i] === 'skip' ? 'bg-zinc-800/50 border-zinc-700 opacity-50' : 'bg-zinc-800/30 border-zinc-700'}`}>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm text-white truncate">{c.name} <span className="text-zinc-500">- {c.phone}</span></p>
                                                        <p className="text-[10px] text-zinc-500">{c.role} | {c.organization}</p>
                                                    </div>
                                                    <div className="flex items-center gap-1 shrink-0 ml-2">
                                                        <Button size="sm" variant={duplicateAction[i] === 'overwrite' ? 'default' : 'outline'}
                                                            className={`h-7 text-[10px] ${duplicateAction[i] === 'overwrite' ? 'bg-amber-600 hover:bg-amber-700' : 'border-zinc-600'}`}
                                                            onClick={() => setDuplicateAction({...duplicateAction, [i]: 'overwrite'})} data-testid={`dup-overwrite-${i}`}>
                                                            <Check className="w-3 h-3 mr-1" /> Ecraser
                                                        </Button>
                                                        <Button size="sm" variant={duplicateAction[i] === 'skip' ? 'default' : 'outline'}
                                                            className={`h-7 text-[10px] ${duplicateAction[i] === 'skip' ? 'bg-zinc-600' : 'border-zinc-600'}`}
                                                            onClick={() => setDuplicateAction({...duplicateAction, [i]: 'skip'})} data-testid={`dup-skip-${i}`}>
                                                            <X className="w-3 h-3 mr-1" /> Ignorer
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-between items-center pt-2">
                                    <Button variant="ghost" onClick={() => { setImportPreview(null); setImportFile(null); }}>Retour</Button>
                                    <Button onClick={handleImportConfirm} className="tactical-btn" disabled={importLoading} data-testid="import-confirm-btn">
                                        {importLoading ? 'Import...' : `Confirmer l'import (${importPreview.new_contacts.length + Object.values(duplicateAction).filter(v => v === 'overwrite').length})`}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
                <Button
                    variant={selectedCategory === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory('all')}
                    className={selectedCategory === 'all' ? 'bg-blue-600' : ''}
                >
                    Tous ({contacts.length})
                </Button>
                {categories.map(cat => {
                    const count = contacts.filter(c => c.category === cat.value).length;
                    if (count === 0) return null;
                    return (
                        <Button
                            key={cat.value}
                            variant={selectedCategory === cat.value ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setSelectedCategory(cat.value)}
                            className={selectedCategory === cat.value ? 'bg-blue-600' : ''}
                        >
                            {cat.label} ({count})
                        </Button>
                    );
                })}
            </div>

            {/* Contacts Grid */}
            {selectedCategory === 'all' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {groupedContacts.map(group => (
                        <Card key={group.value} className="content-card">
                            <CardHeader className="card-header py-3">
                                <CardTitle className={`card-title flex items-center gap-2 text-base ${group.color}`}>
                                    <group.icon className="w-4 h-4" />
                                    {group.label}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="divide-y divide-zinc-800">
                                    {group.contacts.map(contact => (
                                        <div key={contact.id} className="p-3 hover:bg-zinc-800/30">
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <p className="font-medium text-white">{contact.name}</p>
                                                    <p className="text-xs text-zinc-500">{contact.role}</p>
                                                </div>
                                                {isChefDispositif && !contact.is_user && (
                                                    <Button size="sm" variant="ghost" onClick={() => handleDelete(contact.id)} className="text-red-500 h-6 w-6 p-0">
                                                        <Trash2 className="w-3 h-3" />
                                                    </Button>
                                                )}
                                            </div>
                                            <div className="mt-2 space-y-1">
                                                {contact.phone && (
                                                    <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300">
                                                        <Phone className="w-3 h-3" />
                                                        {contact.phone}
                                                    </a>
                                                )}
                                                {contact.radio_channel && (
                                                    <p className="flex items-center gap-2 text-sm text-amber-400">
                                                        <Radio className="w-3 h-3" />
                                                        {contact.radio_channel}
                                                    </p>
                                                )}
                                                {contact.email && (
                                                    <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-300">
                                                        <Mail className="w-3 h-3" />
                                                        {contact.email}
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <Card className="content-card">
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-zinc-800 hover:bg-transparent">
                                    <TableHead className="text-zinc-400">Nom</TableHead>
                                    <TableHead className="text-zinc-400">Fonction</TableHead>
                                    <TableHead className="text-zinc-400">Telephone</TableHead>
                                    <TableHead className="text-zinc-400">Canal radio</TableHead>
                                    <TableHead className="text-zinc-400">Email</TableHead>
                                    {isChefDispositif && <TableHead className="text-zinc-400 text-right">Actions</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredContacts.map(contact => (
                                    <TableRow key={contact.id} className="border-zinc-800">
                                        <TableCell className="font-medium text-white">{contact.name}</TableCell>
                                        <TableCell className="text-zinc-400">{contact.role}</TableCell>
                                        <TableCell>
                                            {contact.phone ? (
                                                <a href={`tel:${contact.phone}`} className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
                                                    <Phone className="w-3 h-3" />
                                                    {contact.phone}
                                                </a>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {contact.radio_channel ? (
                                                <span className="text-amber-400 flex items-center gap-1">
                                                    <Radio className="w-3 h-3" />
                                                    {contact.radio_channel}
                                                </span>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell className="text-zinc-400">{contact.email || '-'}</TableCell>
                                        {isChefDispositif && (
                                            <TableCell className="text-right">
                                                {!contact.is_user ? (
                                                    <Button size="sm" variant="ghost" onClick={() => handleDelete(contact.id)} className="text-red-500">
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                ) : (
                                                    <span className="text-[10px] text-zinc-600">Auto</span>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                                {filteredContacts.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8 text-zinc-500">
                                            <Phone className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                            Aucun contact dans cette categorie
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {contacts.length === 0 && (
                <Card className="content-card">
                    <CardContent className="py-12 text-center">
                        <Phone className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                        <p className="text-zinc-400">Aucun contact dans l'annuaire</p>
                        {isChefDispositif && (
                            <Button className="mt-4 tactical-btn" onClick={() => setDialogOpen(true)}>
                                <Plus className="w-4 h-4 mr-2" />
                                Ajouter le premier contact
                            </Button>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default CrisisDirectoryPage;
